import { useContext, useState } from "react";
import { useNavigate } from "react-router-dom";

import { useSubscriptions, SubscriptionState } from "@/lib/hooks/use-subscriptions";
import { useBillingCancelTrial, useBillingChangePlan, useBillingPortalSession } from "@/lib/api/requests/billing.requests";
import { Button } from "@/ui-components/ui/button";
import { Skeleton } from "@/ui-components/ui/skeleton";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/ui-components/ui/card";
import { Badge } from "@/ui-components/ui/badge";
import { CreditCard, Zap, AlertCircle, CheckCircle2, Calendar, Crown, Building, Mail } from "lucide-react";
import { cn } from "@/lib/utils/cn";
import { motion } from "motion/react";
import { toast } from "@/lib/toast";
import { SubPageLayout } from "@/util-components/page-layout/sub-page-layout";
import { formatDate } from "@/lib/utils/date-formatter";
import { createMetaEventId, getMetaBrowserIds } from "@/lib/meta-pixel/meta-pixel";
import { Context } from "@/util-components/root/context";
import { ChangePlanDialog } from "./components/change-plan-dialog";

const bannerBaseClassName =
  "relative overflow-hidden rounded-3xl border p-6 shadow-sm sm:p-7";

/**
 * Trial Banner Component
 * Modern gradient banner indicating days left on Trial.
 */
export const TrialBanner = ({ accessUntil, onCancelTrial }: { accessUntil?: Date; onCancelTrial?: () => void }) => {
  const now = new Date();
  const daysLeft = accessUntil
    ? Math.max(0, Math.ceil((accessUntil.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)))
    : 0;

  const totalTrialDays = 14;
  const progress = Math.max(0, Math.min(100, 100 - (daysLeft / totalTrialDays) * 100));

  return (
    <div
      className={cn(
        bannerBaseClassName,
        "border-amber-200/70 bg-gradient-to-br from-amber-500 via-orange-500 to-orange-600 text-white",
      )}
    >
      <div className="pointer-events-none absolute -right-20 -top-20 h-56 w-56 rounded-full bg-white/20 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-16 -left-16 h-40 w-40 rounded-full bg-amber-200/30 blur-3xl" />

      <motion.div
        className="absolute inset-0 z-20 pointer-events-none"
        initial={{ x: "-100%", opacity: 0 }}
        animate={{ x: "200%", opacity: 1 }}
        transition={{
          duration: 3,
          ease: "easeInOut",
          delay: 1,
          repeat: Infinity,
          repeatDelay: 5,
        }}
      >
        <div className="h-full w-1/2 -skew-x-[30deg] transform bg-gradient-to-r from-transparent via-white/20 to-transparent" />
      </motion.div>

      <div className="relative z-10 flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
        <div className="flex max-w-2xl flex-1 flex-col items-start">
          <div className="mb-4 inline-flex items-center gap-2 rounded-full bg-white/15 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-[0.16em] text-white ring-1 ring-white/25 backdrop-blur-md">
            <Zap className="h-4 w-4 text-white" />
            Testphase aktiv
          </div>
          <h2 className="text-2xl font-semibold tracking-tight text-white sm:text-[28px]">
            Deine Testphase endet bald.
          </h2>
          <p className="mt-2 max-w-xl text-sm leading-relaxed text-orange-50 sm:text-base">
            Noch{" "}
            <strong className="font-semibold text-white">
              {daysLeft} {daysLeft === 1 ? "Tag" : "Tage"}
            </strong>{" "}
            bis dein Testzeitraum endet.
          </p>
        </div>

        <div className="w-full max-w-md rounded-2xl border border-white/15 bg-black/10 p-4 shadow-inner backdrop-blur-sm">
          <div className="mb-3 flex items-center justify-between text-sm font-semibold text-white">
            <span>Verbleibende Zeit</span>
            <span>
              {daysLeft} {daysLeft === 1 ? "Tag" : "Tage"}
            </span>
          </div>
          <div className="h-2.5 w-full overflow-hidden rounded-full bg-black/20">
            <motion.div
              className="h-full rounded-full bg-white"
              initial={{ width: 0 }}
              animate={{ width: `${progress}%` }}
              transition={{ duration: 1, ease: "easeOut" }}
            />
          </div>

          {onCancelTrial && (
            <Button
              variant="ghost"
              className="mt-4 h-10 rounded-xl px-4 text-sm font-semibold text-white hover:bg-white/15 hover:text-white"
              onClick={onCancelTrial}
            >
              Testphase kündigen
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

/**
 * Active Plan Banner Component
 * Modern gradient banner shown for active (non-trial, non-overdue) basic or pro plans.
 */
export const ActivePlanBanner = ({ subscription }: { subscription: SubscriptionState }) => {
  const isPro = subscription.isPro;
  const isCustom = subscription.type === 'custom';

  let config;

  if (isCustom) {
    config = {
      gradient: "from-slate-800 via-slate-900 to-slate-950",
      iconBg: "bg-white/10 ring-white/10",
      icon: <Building className="w-4 h-4 text-white" />,
      badgeText: "Custom Plan",
      title: "Custom Setup bereit",
      description: "Deine maßgeschneiderte Lösung ist eingerichtet. Du hast volle Kontrolle.",
      bgBlur1: "bg-white/5",
      bgBlur2: "bg-slate-700/20",
      border: "border-slate-700/60",
    };
  } else if (isPro) {
    config = {
      gradient: "from-violet-600 via-purple-600 to-fuchsia-600",
      iconBg: "bg-white/20 ring-white/30",
      icon: <Crown className="w-4 h-4 text-white" />,
      badgeText: "Premium Plan aktiv",
      title: "Pro-Power aktiviert",
      description: "Du hast Zugriff auf alle Premium-Features, exklusive Templates und vieles mehr.",
      bgBlur1: "bg-white/20",
      bgBlur2: "bg-violet-400/20",
      border: "border-violet-300/30",
    };
  } else {
    config = {
      gradient: "from-blue-500 via-blue-600 to-cyan-600",
      iconBg: "bg-white/20 ring-white/30",
      icon: <CheckCircle2 className="w-4 h-4 text-white" />,
      badgeText: "Standard Plan aktiv",
      title: "Willkommen an Bord!",
      description: "Dein Basic-Account ist startklar. Erstelle professionelle Grafiken im Handumdrehen.",
      bgBlur1: "bg-white/20",
      bgBlur2: "bg-blue-300/20",
      border: "border-blue-300/30",
    };
  }

  return (
    <div className={cn(bannerBaseClassName, "bg-gradient-to-br text-white", config.gradient, config.border)}>
      <div className={cn("pointer-events-none absolute -right-20 -top-20 h-56 w-56 rounded-full blur-3xl", config.bgBlur1)} />
      <div className={cn("pointer-events-none absolute -bottom-16 -left-16 h-40 w-40 rounded-full blur-3xl", config.bgBlur2)} />

      <motion.div
        className="absolute inset-0 z-20 pointer-events-none"
        initial={{ x: "-100%", opacity: 0 }}
        animate={{ x: "200%", opacity: 1 }}
        transition={{
          duration: 3,
          ease: "easeInOut",
          delay: 1,
          repeat: Infinity,
          repeatDelay: 5,
        }}
      >
        <div className="h-full w-1/2 -skew-x-[30deg] transform bg-gradient-to-r from-transparent via-white/20 to-transparent" />
      </motion.div>

      <div className="relative z-10 flex flex-col items-start">
        <div
          className={cn(
            "mb-4 inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-[11px] font-semibold uppercase tracking-[0.16em] shadow-sm ring-1 backdrop-blur-md",
            config.iconBg,
          )}
        >
          {config.icon}
          <span className="text-white">{config.badgeText}</span>
        </div>
        <h2 className="text-2xl font-semibold tracking-tight text-white sm:text-[28px]">{config.title}</h2>
        <p className="mt-2 max-w-xl text-sm leading-relaxed text-white/90 sm:text-base">{config.description}</p>
      </div>
    </div>
  );
};

const getPlanName = (type?: string) => {
  switch (type) {
    case "pro": return "Premium Plan";
    case "basic": return "Standard Plan";
    case "custom": return "Custom Plan";
    default: return "Kein Plan";
  }
};

const getStatusBadge = (subscription: SubscriptionState) => {
  if (subscription.isTrialing) return <Badge className="bg-amber-500 hover:bg-amber-600 px-3 py-1 text-xs">Testphase</Badge>;
  if (subscription.isOverdue) return <Badge variant="destructive" className="px-3 py-1 text-xs">Zahlung überfällig</Badge>;
  if (subscription.isCanceled) return <Badge variant="secondary" className="px-3 py-1 text-xs bg-gray-200 text-gray-700">Gekündigt</Badge>;
  if (subscription.isActive) return <Badge className="bg-emerald-500 hover:bg-emerald-600 px-3 py-1 text-xs">Aktiv</Badge>;
  return <Badge variant="secondary" className="px-3 py-1 text-xs">Inaktiv</Badge>;
};

type PlanFeature = string | { 
  text: string; 
  badge?: string; 
  link?: string; 
};

const BASIC_FEATURES: PlanFeature[] = [
  "Unbegrenzte Social Media Posts",
  "Zugriff auf alle Templates",
  "Posten auf Instagram, Facebook & WhatsApp",
  "Planung & Auto-Posting",
  "Sponsoren-Management",
];

const PRO_FEATURES: PlanFeature[] = [
  "Eigene Templates erstellen und bearbeiten",
  "Posts ohne Wasserzeichen",
  "KI Bildunterschrift",
  "KI Hintergrund-Entfernung",
  { text: "KI Analytics & Insights", badge: "Bald" },
];

const CUSTOM_FEATURES: PlanFeature[] = [
  "Individuelle Templates",
  { text: "Verbesserter Support", link: "mailto:support@sportypost.io" },
];

const BillingDashboardSkeleton = () => {
  return (
    <div className="space-y-4 sm:space-y-6">
      <Card className="overflow-hidden border-gray-200 shadow-sm">
        <CardContent className="relative bg-gradient-to-br from-gray-100 via-gray-50 to-white p-6 sm:p-7">
          <div className="space-y-6">
            <div className="space-y-3">
              <Skeleton className="h-6 w-36 rounded-full" />
              <Skeleton className="h-8 w-72 max-w-full" />
              <Skeleton className="h-4 w-full max-w-xl" />
            </div>
            <div className="max-w-md space-y-3 rounded-2xl border border-white/70 bg-white/60 p-4">
              <div className="flex items-center justify-between gap-4">
                <Skeleton className="h-4 w-28" />
                <Skeleton className="h-4 w-16" />
              </div>
              <Skeleton className="h-2.5 w-full rounded-full" />
              <Skeleton className="h-10 w-40 rounded-xl" />
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="overflow-hidden border-gray-200 shadow-sm">
        <CardHeader className="space-y-5 border-b border-gray-100 bg-gradient-to-br from-gray-50 via-white to-gray-50/60 px-4 py-4 sm:px-6 sm:py-6">
          <div className="flex items-start justify-between gap-4">
            <div className="space-y-2">
              <Skeleton className="h-6 w-40" />
              <Skeleton className="h-4 w-64 max-w-full" />
            </div>
            <Skeleton className="h-6 w-20 rounded-full" />
          </div>
        </CardHeader>

        <CardContent className="space-y-6 p-4 sm:space-y-8 sm:p-6">
          <div className="grid gap-6 md:grid-cols-2 md:gap-8">
            <div className="space-y-5">
              <div className="space-y-2">
                <Skeleton className="h-3 w-24" />
                <Skeleton className="h-6 w-36" />
              </div>
              <div className="space-y-2">
                <Skeleton className="h-3 w-44" />
                <Skeleton className="h-5 w-40" />
              </div>
            </div>

            <div className="space-y-4 rounded-2xl border border-gray-200 bg-gray-50/60 p-4 sm:p-5">
              <Skeleton className="h-5 w-36" />
              <div className="space-y-3">
                {Array.from({ length: 5 }).map((_, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <Skeleton className="h-2 w-2 rounded-full" />
                    <Skeleton className="h-4 flex-1" />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </CardContent>

        <CardFooter className="flex flex-col-reverse gap-3 border-t border-gray-100 bg-white px-4 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-6 sm:py-6">
          <Skeleton className="h-10 w-full rounded-xl sm:w-44" />
          <Skeleton className="h-10 w-full rounded-xl sm:w-40" />
        </CardFooter>
      </Card>
    </div>
  );
};

/**
 * Pure display component for the Billing Dashboard.
 * Useful for Storybook mocking.
 */
export const BillingDashboardView = ({ 
  subscription,
  onManageBilling,
  onChangePlan,
  onCancelTrial
}: { 
  subscription: SubscriptionState;
  onManageBilling: () => void;
  onChangePlan: () => void;
  onCancelTrial?: () => void;
}) => {
  const isCustom = subscription.type === 'custom';
  const features = isCustom ? CUSTOM_FEATURES : (subscription.isPro ? PRO_FEATURES : BASIC_FEATURES);
  const hasPlanData = Boolean(subscription.type || subscription.status || subscription.accessUntil);

  return (
     <div className="space-y-4 sm:space-y-6">
       {subscription.isTrialing && (
          <TrialBanner accessUntil={subscription.accessUntil} onCancelTrial={onCancelTrial} />
       )}

       {subscription.isActive && !subscription.isTrialing && !subscription.isOverdue && !subscription.isCanceled && (
          <ActivePlanBanner subscription={subscription} />
       )}

       {subscription.isOverdue && (
           <div className="flex items-start gap-3 rounded-2xl border border-red-100 bg-red-50/60 p-4 text-sm text-red-800 shadow-sm">
              <AlertCircle className="mt-0.5 h-5 w-5 shrink-0" />
              <div>
                 <h4 className="text-sm font-semibold">Zahlungsproblem</h4>
                 <p className="mt-1 leading-relaxed">
                   {subscription.status === "grace_period"
                     ? "Die letzte Zahlung konnte nicht abgewickelt werden. Dein Zugang ist noch vorübergehend aktiv, aber bitte aktualisiere jetzt deine Zahlungsdaten im Kundenportal."
                     : "Die letzte Zahlung konnte nicht abgewickelt werden. Bitte aktualisiere deine Zahlungsdaten im Kundenportal, um eine Unterbrechung des Dienstes zu vermeiden."}
                 </p>
              </div>
           </div>
       )}

       <Card className="overflow-hidden rounded-2xl border-gray-200 shadow-sm">
         <CardHeader className="space-y-5 border-b border-gray-100 bg-gradient-to-br from-gray-50 via-white to-gray-50/60 px-4 py-4 sm:px-6 sm:py-6">
           <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
             <div>
               <CardTitle className="text-xl font-semibold tracking-tight text-gray-900 sm:text-2xl">Aktueller Plan</CardTitle>
               <CardDescription className="mt-1 text-sm leading-relaxed text-gray-500">
                 {hasPlanData
                   ? "Deine aktuelle Mitgliedschaft und die wichtigsten Abrechnungsinformationen."
                   : "Momentan ist noch kein aktiver Plan hinterlegt."}
               </CardDescription>
             </div>
             <div>{getStatusBadge(subscription)}</div>
           </div>
         </CardHeader>
         <CardContent className="p-4 sm:p-6">
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2 md:gap-8">
               <div className="space-y-5">
                  <div>
                    <span className="mb-1 block text-[11px] font-semibold uppercase tracking-[0.16em] text-gray-400">Dein Tarif</span>
                    <div className="flex items-center gap-3">
                       <span className="text-xl font-semibold text-gray-900">{getPlanName(subscription.type)}</span>
                    </div>
                  </div>

                  {subscription.accessUntil && (
                    <div>
                       <span className="mb-1 block text-[11px] font-semibold uppercase tracking-[0.16em] text-gray-400">
                          {subscription.isCanceled ? 'Zugriff bis' : 'Nächste Abrechnung / Verlängerung'}
                       </span>
                       <div className="flex items-center gap-2 text-sm font-medium text-gray-700">
                          <Calendar className="h-4 w-4 text-gray-400" />
                          {formatDate(subscription.accessUntil.toString())}
                       </div>
                    </div>
                  )}
               </div>

               <div className="space-y-3 rounded-2xl border border-gray-200 bg-gray-50/60 p-4 sm:p-5">
                  <h4 className="flex items-center gap-2 text-sm font-semibold text-gray-900">
                     <CheckCircle2 className="h-4 w-4 text-emerald-500" />
                     Inklusive Funktionen
                  </h4>
                  <ul className="space-y-2.5 text-sm text-gray-600">
                     {(subscription.isPro || isCustom) && (
                      <li className="mb-1 flex items-start gap-2 font-semibold text-slate-900">
                          {isCustom ? "Alle Pro-Features" : "Alles im Basic-Paket"}
                       </li>
                     )}

                     {features.map((feature, i) => (
                       <li key={i} className="flex items-start gap-2">
                          <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-blue-400"></span>
                          <span className="leading-relaxed">
                            {typeof feature === 'string' ? feature : (
                              <span className="text-slate-400">
                                {feature.link ? (
                                    <a href={feature.link} className="hover:underline text-blue-600 font-medium">
                                        {feature.text}
                                    </a>
                                ) : (
                                    feature.text
                                )}
                                {feature.badge && (
                                    <span className="ml-1 rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-slate-500">
                                      {feature.badge}
                                    </span>
                                )}
                              </span>
                            )}
                          </span>
                       </li>
                     ))}
                  </ul>
               </div>
            </div>
         </CardContent>
         <CardFooter className="flex flex-col-reverse gap-3 border-t border-gray-100 bg-white px-4 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-6 sm:py-6">
            {subscription.hasStripeId ? (
              <Button 
                 className="h-10 w-full rounded-xl bg-blue-600 px-4 text-sm font-semibold text-white shadow-md shadow-blue-500/20 transition-all hover:bg-blue-700 sm:w-auto sm:px-6"
                 onClick={onManageBilling}
              >
                 <CreditCard className="mr-2 h-4 w-4" />
                 Zum Kundenportal
              </Button>
            ) : (
              <div className="flex w-full flex-col gap-2 sm:w-auto">
                 <span className="text-sm text-gray-500">Wünscht du Änderungen am Tarif?</span>
                 <Button 
                    variant="outline"
                    className="h-10 w-full rounded-xl border-gray-200 bg-white px-4 text-sm font-semibold text-gray-700 shadow-sm transition-all hover:bg-gray-50 hover:text-blue-600"
                    asChild
                 >
                    <a href="mailto:support@sportypost.io?subject=Anfrage%20Tarifänderung">
                      <Mail className="mr-2 h-4 w-4" />
                      Support kontaktieren
                    </a>
                 </Button>
              </div>
            )}
            
            {subscription.type !== 'custom' && subscription.hasStripeId && !subscription.isCanceled && (
              <Button 
                 variant="outline" 
                 className="h-10 w-full rounded-xl border-gray-200 px-4 text-sm font-semibold text-gray-700 transition-all hover:bg-gray-50 hover:text-blue-600 sm:w-auto sm:px-6"
                 onClick={onChangePlan}
              >
                 Plan wechseln / Upgraden
              </Button>
            )}
         </CardFooter>
       </Card>
     </div>
  )
}

/**
 * Main wrapper page connecting the state with the view.
 */
export default function OrganizationBillingPage() {
   const { user } = useContext(Context);
   const subscription = useSubscriptions();
   const navigate = useNavigate();
   const [isChangePlanDialogOpen, setIsChangePlanDialogOpen] = useState(false);

   const { mutateAsync: createPortalSession } = useBillingPortalSession();
   const { mutateAsync: changePlan } = useBillingChangePlan();
   const { mutateAsync: cancelTrial } = useBillingCancelTrial();

   const handleManageBilling = async () => {
     try {
       const { url } = await createPortalSession();
       window.location.href = url;
     } catch (error) {
       toast.error("Kundenportal fehlgeschlagen", { error });
     }
   };

   const handleChangePlan = () => {
     setIsChangePlanDialogOpen(true);
   };

   const handleConfirmChangePlan = async (plan: "basic" | "pro") => {
     try {
       const metaEventId = createMetaEventId();
       const { fbp, fbc } = getMetaBrowserIds();
       const { url } = await changePlan({
         plan,
         metaEventId,
         fbp,
         fbc,
         metaSourceUrl: window.location.href,
       });
       window.location.href = url;
     } catch (error) {
       toast.error("Planwechsel fehlgeschlagen", { error });
     }
   };

   const handleCancelTrial = async () => {
     try {
       await cancelTrial();
       window.location.reload();
       toast.success("Trial wurde gekündigt.");
     } catch (error) {
       toast.error("Trial-Kündigung fehlgeschlagen", { error });
     }
   };

   return (
      <SubPageLayout
        pageTitle="Abonnement"
        parentPageTitle="Dein Verein"
        parentPageCallback={() => navigate("/vereine")}
        navigateBackCallback={() => navigate("/vereine")}
        icon={<CreditCard className="h-6 w-6" />}
      >
        <div className="mx-auto flex w-full max-w-5xl flex-col gap-4 px-4 py-4 sm:gap-6 sm:px-6 sm:py-8">
           {user ? (
             <BillingDashboardView
                subscription={subscription}
                onManageBilling={handleManageBilling}
                onChangePlan={handleChangePlan}
                onCancelTrial={handleCancelTrial}
             />
           ) : (
             <BillingDashboardSkeleton />
           )}

           <ChangePlanDialog 
             isOpen={isChangePlanDialogOpen} 
             onClose={() => setIsChangePlanDialogOpen(false)} 
             currentPlan={subscription.type as any} 
             onSelectPlan={handleConfirmChangePlan} 
             hasUsedTrial={subscription.hasUsedTrial}
           />
        </div>
      </SubPageLayout>
   );
}
